import {keyMirror} from 'utils/DBUtils';

export const Types = keyMirror({
    PICK_ORIGINAL: null,
    PICKED_ORIGINAL: null,
    SET_EXISTING_IMAGE: null,
    SHOW_IMAGE_EDIT_FORM_ERRORS: null,

    UPDATE_PENDING_IMAGE_UPLOAD: null,

    UPLOAD_ORIGINAL: null,
    UPLOAD_SUCCESS: null,

    UPLOADED_ORIGINAL: null,
    END_UPLOAD_ORIGINAL: null,
    UPLOAD_ORIGINAL_AND_CREATE_IMAGE: null,
    SET_UPLOADING_IMAGES_FOR: null,

    START_EDIT_IMAGE: null,
    END_EDIT_IMAGE: null,
    END_THEN_EDIT_IMAGE: null,
    DELETE_IMAGE: null,
    DELETED_IMAGE: null,
    TOGGLE_IMAGES_PAGE_VIEW: null,

    GENERATE_IMAGES: null,
    STARTING_GENERATE_IMAGES: null,
    END_GENERATE_IMAGES: null,
    UPLOAD_ORIGINAL_AND_GENERATE_IMAGES: null,

    CREATE_IMAGE: null,
    CREATED_IMAGE: null,

    OPEN_NATIVE_UPLOAD_MODAL: null,
    CLOSE_NATIVE_UPLOAD_MODAL: null,
    NATIVE_UPLOAD_IMAGE: null,

    OPEN_NATIVE_RECROP_MODAL: null,
    CLOSE_NATIVE_RECROP_MODAL: null,

    SAVE_IMAGE_DATA: null,
    SAVED_IMAGE_DATA: null,

    CHANGE_SELECTION: null,

    OPEN_CONFIRM_CANCEL_MODAL: null,
    CLOSE_CONFIRM_CANCEL_MODAL: null,

    RECEIVED_SIBLING_IMAGES: null,

    REMOVE_IMAGE_FROM_CURRENT_BREAK: null,
    REMOVED_IMAGE_FROM_CURRENT_BREAK: null
});

export const pickOriginalAction = (forResourceId, optionOverrides) => ({
    type: Types.PICK_ORIGINAL,
    forResourceId: forResourceId,
    optionOverrides: optionOverrides
});

export const pickedOriginalAction = (pendingOriginalFile) => ({
    type: Types.PICKED_ORIGINAL,
    pendingOriginalFile: pendingOriginalFile
});

export const setExistingImageAction = existingImage => ({
    type: Types.SET_EXISTING_IMAGE,
    existingImage
});

export const showImageEditFormErrorsAction = errors => ({
    type: Types.SHOW_IMAGE_EDIT_FORM_ERRORS,
    errors
});

export const updatePendingImageUploadAction = (field, value) => ({
    type: Types.UPDATE_PENDING_IMAGE_UPLOAD,
    field,
    value
});

export const uploadOriginalAction = (imageInfo) => ({
    type: Types.UPLOAD_ORIGINAL,
    imageInfo: imageInfo
});

export const uploadOriginalAndCreateImageAction = (imageType, rootResource, forResourceId, resourcePath, fieldPath, field, parentIds) => ({
    type: Types.UPLOAD_ORIGINAL_AND_CREATE_IMAGE,
    forResourceId: forResourceId,
    imageType: imageType,
    rootResource: rootResource,
    resourcePath: resourcePath,
    fieldPath: fieldPath,
    field: field,
    parentIds: parentIds
});

export const uploadSuccessAction = image => ({
    type: Types.UPLOAD_SUCCESS,
    image
});

export const uploadedOriginalAction = (image) => ({
    type: Types.UPLOADED_ORIGINAL,
    image: image
});

export const setUploadingImagesFor = uploadingImagesFor => ({
    type: Types.SET_UPLOADING_IMAGES_FOR,
    uploadingImagesFor
});

export const endUploadOriginalAction = () => ({
    type: Types.END_UPLOAD_ORIGINAL
});

export const startEditImageAction = (imageId) => ({
    type: Types.START_EDIT_IMAGE,
    imageId: imageId
});

export const endEditImageAction = () => ({
    type: Types.END_EDIT_IMAGE,
});

export const endThenEditImageAction = (nextEditImageId) => ({
    type: Types.END_THEN_EDIT_IMAGE,
    nextEditImageId: nextEditImageId
});

export const deleteImageAction = (imageId) => ({
    type: Types.DELETE_IMAGE,
    imageId: imageId
});

export const deletedImageAction = () => ({
    type: Types.DELETED_IMAGE
});

export const toggleImagesPageViewAction = (count, page) => ({
    type: Types.TOGGLE_IMAGES_PAGE_VIEW,
    count: count,
    page: page
});

export const createImageAction = (originalImageId, imageType, rootResource, forResourceId, resourcePath, fieldPath, field, parentIds) => ({
    type: Types.CREATE_IMAGE,
    originalImageId: originalImageId,
    imageType: imageType,
    rootResource: rootResource,
    forResourceId: forResourceId,
    resourcePath: resourcePath,
    fieldPath: fieldPath,
    field: field,
    parentIds: parentIds
});

export const createdImageAction = (image) => ({
    type: Types.CREATED_IMAGE,
    image: image
});

// Opens the native (non-Filestack) upload modal for a field, carrying the same
// resource-hierarchy context uploadOriginalAndCreateImageAction carries -- the modal
// itself is mounted once, at the page level (see ImageUploadModalContainer), so this is
// the bridge that lets a deeply-nested field (hoc/BreakImagePicker) tell it what to open
// for and, on confirm, what to attach the result to. Not called out explicitly by the
// plan (which only specifies the NATIVE_UPLOAD_IMAGE action fired on confirm), but needed
// to open the modal at all given that mounting location -- see the plan's write-up.
export const openNativeUploadModalAction = (imageType, rootResource, forResourceId, resourcePath, fieldPath, field, parentIds) => ({
    type: Types.OPEN_NATIVE_UPLOAD_MODAL,
    imageType: imageType,
    rootResource: rootResource,
    forResourceId: forResourceId,
    resourcePath: resourcePath,
    fieldPath: fieldPath,
    field: field,
    parentIds: parentIds
});

export const closeNativeUploadModalAction = () => ({
    type: Types.CLOSE_NATIVE_UPLOAD_MODAL
});

// Fired once the native upload modal's onConfirm resolves with either a cropped `file`
// (device upload) or a `mediaUrl` (URL upload). Carries the same resource-hierarchy
// payload uploadOriginalAndCreateImageAction does, plus the {file|mediaUrl, preCropped,
// name, filename, altAttribute, source, link} produced by the modal's Info step (see
// components/images/ImageUploadInfoStep.jsx).
export const nativeUploadImageAction = (imageType, rootResource, forResourceId, resourcePath, fieldPath, field, parentIds, upload) => ({
    type: Types.NATIVE_UPLOAD_IMAGE,
    imageType: imageType,
    rootResource: rootResource,
    forResourceId: forResourceId,
    resourcePath: resourcePath,
    fieldPath: fieldPath,
    field: field,
    parentIds: parentIds,
    file: upload.file,
    mediaUrl: upload.mediaUrl,
    preCropped: upload.preCropped,
    name: upload.name,
    filename: upload.filename,
    altAttribute: upload.altAttribute,
    source: upload.source,
    link: upload.link
});

// Opens the native re-crop modal (§10) for an image field's "Edit Image"
// affordance -- carries the same resource-hierarchy context openNativeUploadModalAction
// does, plus the originalImageId whose bytes ImageRecropModalContainer fetches (via
// GET /image/fetch, same as a Link-tab source -- the original lives in S3, a different
// origin from this app, so it needs the same same-origin-safe fetch §7 built, not a plain
// <img> load). Mounted once at the page level (see ImageRecropModalContainer), mirroring
// how ImageUploadModalContainer/openNativeUploadModalAction bridge a deeply-nested field
// to a page-level modal.
export const openNativeRecropModalAction = (imageType, rootResource, forResourceId, resourcePath, fieldPath, field, parentIds, originalImageId) => ({
    type: Types.OPEN_NATIVE_RECROP_MODAL,
    imageType: imageType,
    rootResource: rootResource,
    forResourceId: forResourceId,
    resourcePath: resourcePath,
    fieldPath: fieldPath,
    field: field,
    parentIds: parentIds,
    originalImageId: originalImageId
});

export const closeNativeRecropModalAction = () => ({
    type: Types.CLOSE_NATIVE_RECROP_MODAL
});

export const saveImageDataAction = (image) => ({
    type: Types.SAVE_IMAGE_DATA,
    image: image
});

export const savedImageDataAction = () => ({
    type: Types.SAVED_IMAGE_DATA
});

export const changeImageSelectionAction = nextSelectionIndex => ({
    type: Types.CHANGE_SELECTION,
    nextSelectionIndex
});

export const openConfirmCancelModalAction = () => ({
    type: Types.OPEN_CONFIRM_CANCEL_MODAL
});

export const closeConfirmCancelModalAction = () => ({
    type: Types.CLOSE_CONFIRM_CANCEL_MODAL
});

export const receivedSiblingImages = (siblings) => ({
    type: Types.RECEIVED_SIBLING_IMAGES,
    siblings: siblings
});

export const removeImageFromCurrentBreakAction = (image) => ({
    type: Types.REMOVE_IMAGE_FROM_CURRENT_BREAK,
    image: image
});

export const removedImageFromCurrentBreakAction = () => ({
    type: Types.REMOVED_IMAGE_FROM_CURRENT_BREAK
});

export const generateImagesAction = (breakId, originalImageId, types, fields) => ({
  type: Types.GENERATE_IMAGES,
  breakId: breakId,
  originalImageId: originalImageId,
  types: types,
  fields: fields
});

export const startGenerateImagesAction = () => ({
  type: Types.STARTING_GENERATE_IMAGES
});

export const endGenerateImagesAction = () => ({
  type: Types.END_GENERATE_IMAGES
});

export const uploadOriginalAndGenerateImagesAction = (breakId, originalImageId, types, fields) => ({
  type: Types.UPLOAD_ORIGINAL_AND_GENERATE_IMAGES,
  breakId: breakId,
  originalImageId: originalImageId,
  types: types,
  fields: fields
});



// WEBPACK FOOTER //
// ./public/pages/Images/actions.js